/**
 * Hi! Welcome to the Phaneroo Prayer Requests prayer-requests store.
 *
 * - All API fetch requests are actually handled inside the `prayer-requests.service.js` file.
 * - Every action has a corresponding $axios request that performs validation and request handling.
 * - Tests for this store module are located in './tests/prayer-requests.test.js'
 *
 * @todo
 * - Write tests for prayer-requests store module
 * - Perform data transforms for dashboard UI
 * - Require collect token in $axios instance from session storage
 *   instead of passing it as param.
 */

import '../../typedefs'
import { getPrayerRequests, getPrayerRequestById, createPrayerRequest, updatePrayerRequest, deletePrayerRequest, getUnreadPrayerRequests, markAllAsRead } from '../../services/prayer-requests.service'
import assign from 'lodash-es/assign'
import { indexArrayBy } from '../../utils'
import { isArray } from 'lodash-es'

const __DEV__ = process.env.NODE_ENV !== 'production'

export const basePrayerRequestsState = {
  /**
   * @type {PrayerRequest[]}
   */
  prayerRequests: [],
  /**
   * @type {PrayerRequest}
   */
  prayerRequest: null,
  /**
   * @type {Number}
   */
  pagination: {
    count: 0,
    pageSize: 10,
    currentPage: 1,
    nextPage: 2
  },
  totalUnread: 0
}

export default {
  namespaced: true,
  state: basePrayerRequestsState,
  getters: {
    pageSize: state => state.pagination.pageSize,
    currentPage: state => state.pagination.currentPage,
    totalUnread: state => state.totalUnread,
    count: state => state.pagination.count,
    /**
     * @returns {PrayerRequest}
     */
    prayerRequest: state => state.prayerRequest,
    /**
     * @returns {PrayerRequest[]}
     */
    prayerRequests: state =>
      state.prayerRequests
        .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)),
    unreadPrayerRequests: state =>
      state.prayerRequests
        .filter(prayerRequest => prayerRequest.status === 'Unread')
        .sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)),
    prayerReq: state =>
      state.prayerRequests.length
        ? indexArrayBy(
          state.prayerRequests
            .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
          , 'id')
        : {}
  },
  mutations: {
    /**
     * Sets pagination data
     * @param {Object} state state
     * @param {PrayerRequest[]} payload  Prayer requests array
     */
    SET_PAGINATION_DATA (state, payload) {
      state.pagination = payload
    },
    /**
     * Sets array of prayer requests to active state
     * @param {Object} state state
     * @param {PrayerRequest[]} payload  Prayer requests array
     */
    SET_PRAYER_REQUESTS (state, payload) {
      state.prayerRequests = payload.PrayerRequestCollection.rows
      state.pagination.count = payload.PrayerRequestCollection.count
    },
    /**
     * Sets specific prayer request to active state
     * @param {Object} state state
     * @param {PrayerRequest} payload New prayer request object
     */
    SET_PRAYER_REQUEST (state, payload) {
      state.prayerRequest = payload
    },
    /**
     * Adds new prayer request to active state
     * @param {Object} state state
     * @param {PrayerRequest} payload New prayer request object
     */
    ADD_PRAYER_REQUEST (state, payload) {
      const collection = state.prayerRequests
      collection && Array.isArray(collection)
        ? collection.push(payload)
        : state.prayerRequests = [payload]
    },
    /**
     * Updates specific prayer requests's object state
     * @param {Object} state state
     * @param {Category} payload Category
     */
    UPDATE_PRAYER_REQUEST (state, payload) {
      const targetIndex = state.prayerRequests.findIndex(prayerRequest => prayerRequest.id === payload.id)
      assign(state.prayerRequests[targetIndex], payload)
    },
    /**
     * Deletes specific prayer request from state
     * @param {Object} state state
     * @param {String} id Target prayer request id
     */
    DELETE_PRAYER_REQUEST (state, id) {
      const targetIndex = state.prayerRequests.findIndex(prayerRequest => prayerRequest.id === id)
      state.prayerRequests.splice(targetIndex, 1)
    },
    /**
     * Resets the original state for prayer requests
     * @param {Object} state state
     */
    RESET_PRAYER_REQUESTS (state) {
      assign(state, basePrayerRequestsState)
    },
    /**
     * Sets the total undread prayer request count
     * @param {Object} state state
     */
    SET_UNREAD_PRAYER_REQUESTS_COUNT (state, payload) {
      state.totalUnread = payload
    }
  },
  actions: {
    /**
     * Gets all prayer requests
     * @param {VuexContext} context Store context
     */
    async getPrayerRequests ({ commit, rootState, dispatch }, { pageSize = 10, page }) {
      const authToken = rootState.auth.authToken
      const response = await getPrayerRequests(authToken, { pageSize, page })
      dispatch('getUnreadPrayerRequests')
      const prayerRequests = response.data
      commit('SET_PRAYER_REQUESTS', prayerRequests.data)

      const { nextPage, currentPage } = prayerRequests.data

      commit('SET_PAGINATION_DATA', {
        pageSize: currentPage.pageSize,
        currentPage: currentPage.page,
        nextPage: nextPage.page,
        count: prayerRequests.data.PrayerRequestCollection.count
      })
    },
    /**
     * Gets all prayer requests
     * @param {VuexContext} context Store context
     */
    async getAllPrayerRequests ({ commit, rootState, dispatch }) {
      const authToken = rootState.auth.authToken
      const response = await getPrayerRequests(authToken)
      dispatch('getUnreadPrayerRequests')
      const prayerRequests = response.data
      commit('SET_PRAYER_REQUESTS', prayerRequests.data)
    },
    /**
     * Gets all prayer requests
     * @param {VuexContext} context Store context
     */
    async getUnreadPrayerRequests ({ commit, rootState }) {
      const authToken = rootState.auth.authToken
      const response = await getUnreadPrayerRequests(authToken)
      const count = response.data
      commit('SET_UNREAD_PRAYER_REQUESTS_COUNT', count.response)
    },
    /**
     * Gets a user's prayer requests
     * @param {VuexContext} context Store context
     */
    async getUserPrayerRequests ({ commit, rootState }, userId) {
      const authToken = rootState.auth.authToken
      const response = await getPrayerRequests(authToken, { user_id: userId })
      const prayerRequests = response.data
      commit('SET_PRAYER_REQUESTS', prayerRequests.data)
    },
    /**
     * Gets a single prayer request
     * @param {VuexContext} context
     * @param {String} id Prayer request Id
     */
    async getPrayerRequest ({ commit, rootState }, id) {
      const authToken = rootState.auth.authToken
      const response = await getPrayerRequestById(authToken, id)
      const prayerRequest = response.data.data
      commit('SET_PRAYER_REQUEST', prayerRequest)
    },
    /**
     * Creates a new prayer request
     * @param {VuexCategory} context Store context
     * @param {PrayerRequest} payload new prayer request data
     */
    async createPrayerRequest ({ commit, rootState }, payload) {
      const authToken = rootState.auth.authToken
      const response = await createPrayerRequest(authToken, payload)
      const prayerRequest = response.data
      commit('ADD_PRAYER_REQUEST', prayerRequest.data)
    },
    /**
     * Updates a prayer request
     * @param {VuexCategory} context Store context
     * @param {{ id: String, payload: PrayerRequest }} payload Category data
     */
    async updatePrayerRequest ({ commit, rootState, dispatch }, { id, payload }) {
      const authToken = rootState.auth.authToken
      const response = await updatePrayerRequest(authToken, id, payload)
      dispatch('getUnreadPrayerRequests')
      const [prayerRequest] = response.data.data
      commit('UPDATE_PRAYER_REQUEST', prayerRequest)
      commit('SET_PRAYER_REQUEST', prayerRequest)
    },
    /**
     * Deletes a prayer request
     * @param {VuexCategory} context Store context
     * @param {String} id prayer request id
     */
    async deletePrayerRequest ({ commit, rootState, dispatch }, id) {
      const authToken = rootState.auth.authToken
      const response = await deletePrayerRequest(authToken, id)
      dispatch('getUnreadPrayerRequests')
      const { message } = response
      __DEV__ && console.log(`Deleted prayer request of id: ${id}:`, message)
      commit('DELETE_PRAYER_REQUEST', id)
    },

    /**
     * Updates all prayer requests as read
     * @param {import('vuex').Action} context
     * @param {string[]} ids
     */
    async markAllAsRead ({ commit, rootState, getters }, payload) {
      const authToken = rootState.auth.authToken
      const response = await markAllAsRead(authToken, payload)
      const updatedPrayerRequests = response.data.data

      if (isArray(updatedPrayerRequests)) {
        updatedPrayerRequests.forEach((prayerRequest) => {
          commit('UPDATE_PRAYER_REQUEST', prayerRequest)
        })
      }
    }
  }
}
